/* ============================================================================
   CRUX REDESIGN — INTERACTIVE LAYER  ·  assets/crux-redesign.js
   Vanilla classic script (IIFE, no ES modules, no libraries) matching
   global.js + the CRUX mockup app.js idiom. Honors prefers-reduced-motion.

   Net-new behaviour Zenith does NOT ship:
     1. SCROLL-REVEAL  — IntersectionObserver engine (<crux-reveal> custom
        element + [data-reveal] / [data-reveal="wipe"] clip-wipe). Fails OPEN.
     2. COUNTDOWN      — flip-clock over [data-countdown="ISO"]; on elapse
        unlocks .cx-drop-card.is-locked → .is-unlocked.
     3. TRY-ON         — [data-tryon] shimmer/loading simulation (FASHN hook).

   Ported verbatim (logic) from docs/storefront_redesign/mockups/app.js
   §1 (reveal), §8 (countdown), §10 (try-on). The custom element is RENAMED
   from 'scroll-reveal' to 'crux-reveal' to keep the CRUX namespace and avoid
   any collision; define() is guarded with customElements.get() because the
   theme editor re-injects section HTML on every edit (a second define throws).

   WIRING: each init (re)runs on the Zenith editor events 'shopify:section:load'
   AND 'shopify:section:reorder' (header-group parity), scoped to event.target
   when present so re-inserts don't double-bind, plus an initial ready() boot.
   Deliberately NOT ported: header / drawers / gallery / sticky-atc / accordion
   / tabs / scratch / freeship / compare / markers / trending / marquee /
   slideshow / deck / stepper / storemap — Zenith ships native equivalents.
   ============================================================================ */
(function () {
  'use strict';

  /* ---- 0. reduced-motion + helpers -------------------------------------- */
  var docEl = document.documentElement;

  var REDUCE = false;
  try {
    REDUCE = window.matchMedia &&
             window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { REDUCE = false; }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else { fn(); }
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts || false); }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  /* ---- 0b. Primary-app coexistence detection ----------------------------
     crux-app.js (the full mockup app.js port — loaded BEFORE this file in
     theme.liquid) already binds scroll-reveal, the countdown flip-clock and
     the try-on simulator, and it registers the <scroll-reveal> custom element.
     This file (crux-redesign.js) carries its OWN copies of those three so it
     stays self-sufficient on any page that ships redesign.js alone — but on a
     page where BOTH load, running them again DOUBLE-binds: two click handlers
     on every [data-tryon] (two render simulations) and two setInterval tick
     loops on every [data-countdown]. We detect the primary app via the custom
     element it defines and let it own reveal / countdown / try-on; this file
     keeps the behaviours it ALONE provides (EON stylist, coord-set, FX slider,
     social proof, reviews, sticky ATC). If the primary app is absent, our
     copies still run — never a missing behaviour. */
  function primaryAppOwns() {
    try {
      return ('customElements' in window) &&
             !!customElements.get('scroll-reveal');
    } catch (e) { return false; }
  }

  /* ========================================================================
     1. SCROLL-REVEAL  (<crux-reveal> + [data-reveal])
        IO → .is-visible, staggered, then unobserve. Above-the-fold reveals
        immediately. Belt-and-suspenders sweep at 1200ms. Fails OPEN.
     ===================================================================== */
  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-visible');
  }
  function collectTargets(root) {
    var t = $all('[data-reveal]', root);
    if (root.hasAttribute && root.hasAttribute('data-reveal')) t.unshift(root);
    return t;
  }
  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || docEl.clientHeight || 0;
    return r.top < vh * 0.95 && r.bottom > 0; // on / near the first screen
  }

  var io = null;
  if (!REDUCE && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8%' });
  }

  function bootReveal(root) {
    var targets = collectTargets(root || document);
    // assign stagger index per group of siblings if not authored
    targets.forEach(function (el) {
      if (!el.style.getPropertyValue('--i') && el.parentNode) {
        var sibs = $all('[data-reveal]', el.parentNode).filter(function (n) {
          return n.parentNode === el.parentNode;
        });
        var idx = sibs.indexOf(el);
        if (idx > 0) el.style.setProperty('--i', String(idx));
      }
    });
    if (io && !REDUCE) {
      targets.forEach(function (el) {
        // Above-the-fold reveals IMMEDIATELY (incl. the hero clip-wipe) so it is
        // never stuck invisible if the observer doesn't fire for an in-view node.
        if (inViewport(el)) el.classList.add('is-visible');
        else io.observe(el);
      });
      // Belt-and-suspenders: anything still hidden but in view shortly after load
      // (late layout / fonts / slow paint) is force-revealed — never stuck.
      setTimeout(function () {
        targets.forEach(function (el) {
          if (el.classList.contains('is-visible')) return;
          if (inViewport(el)) { el.classList.add('is-visible'); io.unobserve(el); }
        });
      }, 1200);
    } else {
      revealAll(targets); // fail-open (no IO / reduced-motion)
    }
  }

  // Custom element <crux-reveal> — self-inits on connect (covers editor
  // re-inserts). Guarded define so a second registration never throws.
  if ('customElements' in window && !customElements.get('crux-reveal')) {
    try {
      customElements.define('crux-reveal', class extends HTMLElement {
        connectedCallback() { bootReveal(this); }
      });
    } catch (e) { /* attribute bootstrap below still covers it */ }
  }

  /* ========================================================================
     2. COUNTDOWN flip-clock  (data-countdown="ISO" — drop / launch datetime)
        Maps days/hrs/mins/secs to .cx-countdown__digit[data-unit] slots,
        flips changed digits, and on elapse unlocks gated drop cards.
     ===================================================================== */
  function initCountdown(root) {
    $all('[data-countdown]', root).forEach(function (clock) {
      if (clock.__cruxCountdownBound) return; // idempotent across re-inits
      clock.__cruxCountdownBound = true;

      var iso = clock.getAttribute('data-countdown');
      var target;
      if (iso) target = new Date(iso).getTime();
      if (!target || isNaN(target)) {
        // HONESTY (vet 2026-07-02): no/invalid datetime → never invent a fake
        // countdown. Hide the clock (and its strip wrapper if it exists) —
        // a drop without a date has nothing to count to.
        clock.hidden = true;
        var box = clock.closest('[data-countdown-wrap]');
        if (box) box.hidden = true;
        return;
      }

      var units = ['days', 'hrs', 'mins', 'secs'];
      var slots = {};
      units.forEach(function (u) {
        slots[u] = $all('.cx-countdown__digit[data-unit="' + u + '"]', clock);
      });
      var prev = {};

      function setUnit(unit, value) {
        var str = pad2(value);
        var digits = slots[unit];
        if (!digits || !digits.length) return;
        var offset = digits.length - str.length;
        for (var i = 0; i < digits.length; i++) {
          var ch = i - offset >= 0 ? str.charAt(i - offset) : '0';
          if (digits[i].textContent !== ch) {
            digits[i].textContent = ch;
            if (!REDUCE) {
              digits[i].classList.remove('is-flipping');
              void digits[i].offsetWidth; // restart animation
              digits[i].classList.add('is-flipping');
            }
          }
        }
      }

      function tick() {
        var diff = target - Date.now();
        if (diff < 0) {
          diff = 0;
          clock.classList.add('is-elapsed');
          // unlock any gated drop cards tied to this clock (drop-gate)
          $all('.cx-drop-card.is-locked').forEach(function (c) {
            c.classList.remove('is-locked');
            c.classList.add('is-unlocked');
          });
        }
        var s = Math.floor(diff / 1000);
        var vals = {
          days: Math.floor(s / 86400),
          hrs:  Math.floor((s % 86400) / 3600),
          mins: Math.floor((s % 3600) / 60),
          secs: s % 60
        };
        units.forEach(function (u) {
          if (prev[u] !== vals[u]) { setUnit(u, vals[u]); prev[u] = vals[u]; }
        });
        if (diff > 0) setTimeout(tick, 1000);
      }
      tick();
    });
  }

  /* ========================================================================
     3. TRY-ON button  (data-tryon ; data-tryon-target="SEL")
        Simulate render → restore label → one-time shimmer sweep on target.
        Real FASHN render wiring lands later; the shimmer + loading state are
        the UI contract.
     ===================================================================== */
  function initTryon(root) {
    $all('[data-tryon]', root).forEach(function (btn) {
      if (btn.__cruxTryonBound) return; // idempotent across re-inits
      btn.__cruxTryonBound = true;

      on(btn, 'click', function (e) {
        e.preventDefault();
        if (btn.classList.contains('is-loading')) return;
        var sel = btn.getAttribute('data-tryon-target');
        var target = sel ? $(sel) : null;
        btn.classList.add('is-loading');
        var label = $('.cx-tryon-btn__label', btn) || btn;
        var prevText = label.textContent;
        var delay = REDUCE ? 200 : 1100;
        setTimeout(function () {
          btn.classList.remove('is-loading');
          label.textContent = prevText;
          if (target) {
            target.classList.add('cx-tryon-target');
            if (!REDUCE) {
              target.classList.remove('is-shimmering');
              void target.offsetWidth;
              target.classList.add('is-shimmering');
              setTimeout(function () { target.classList.remove('is-shimmering'); }, 400);
            }
          }
        }, delay);
      });
    });
  }

  /* ========================================================================
     4. FX SLIDER  (cx-collection-editorial — "The Crux Effect")
        New-Arrivals-style look deck: ← → arrows + drag scroll the track,
        the card nearest the row's reading edge gets .is-active (scale 1) and
        drives the NN / NN counter. Idempotent across editor re-inits.
     ===================================================================== */
  function initFxSlider(root) {
    $all('[data-cx-fx]', root).forEach(function (deck) {
      if (deck.__cruxFxBound) return; // idempotent across re-inits
      deck.__cruxFxBound = true;

      var track = $('[data-cx-fx-track]', deck);
      if (!track) return;
      var cards = $all('.cx-fx__card', track);
      if (!cards.length) return;
      // scope counter + arrows to THIS band so co-existing decks never cross-wire
      var box = deck.closest('.cx-fx') || deck.parentNode || document;
      var count = $('[data-cx-fx-count]', box);
      var prevBtn = $('[data-cx-fx-prev]', box);
      var nextBtn = $('[data-cx-fx-next]', box);
      var collHandle = deck.getAttribute('data-cx-collection') || '';
      var N = cards.length;

      function step() {
        var r = cards[0].getBoundingClientRect();
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 24) || 24;
        return r.width + gap;
      }
      function sync() {
        var tr = track.getBoundingClientRect();
        var ref = tr.left + Math.min(track.clientWidth * 0.5, 200);
        var best = 0, bd = Infinity;
        for (var i = 0; i < N; i++) {
          var cr = cards[i].getBoundingClientRect();
          var d = Math.abs((cr.left + cr.width / 2) - ref);
          if (d < bd) { bd = d; best = i; }
        }
        for (var j = 0; j < N; j++) cards[j].classList.toggle('is-active', j === best);
        if (count) count.textContent = pad2(best + 1) + ' / ' + pad2(N);
      }

      on(nextBtn, 'click', function () { track.scrollBy({ left: step(), behavior: REDUCE ? 'auto' : 'smooth' }); });
      on(prevBtn, 'click', function () { track.scrollBy({ left: -step(), behavior: REDUCE ? 'auto' : 'smooth' }); });

      var raf = false;
      on(track, 'scroll', function () {
        if (raf) return; raf = true;
        window.requestAnimationFrame(function () { raf = false; sync(); });
      }, { passive: true });

      // drag-to-scroll (pointer) — matches the .c-deck grab affordance.
      // Track movement so a DRAG doesn't fire a navigation click on the <a>
      // cards (the mobile "swipe teleports me to a product" bug).
      var down = false, sx = 0, sl = 0, moved = false;
      on(track, 'pointerdown', function (e) {
        // Touch already gets native scroll-snap on .cx-fx__track; running JS
        // drag alongside it double-handles the swipe (janky). Mouse-only, like
        // the c-deck slider.
        if (e.pointerType === 'touch') return;
        if (e.button && e.button !== 0) return;
        down = true; moved = false; sx = e.clientX; sl = track.scrollLeft;
        track.classList.add('is-dragging');
      });
      on(window, 'pointerup', function () { down = false; track.classList.remove('is-dragging'); });
      on(track, 'pointermove', function (e) {
        if (!down) return;
        if (Math.abs(e.clientX - sx) > 6) moved = true;
        track.scrollLeft = sl - (e.clientX - sx);
      });
      // capture-phase: kill the click that ends a drag; otherwise beacon a
      // genuine card click to EON (intelligence: which editorial looks pull).
      on(track, 'click', function (e) {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; return; }
        var card = e.target.closest && e.target.closest('.cx-fx__card');
        if (card && window.EON && window.EON.track) {
          var nm = card.querySelector('.cx-fx__name');
          window.EON.track('fx_card_click', {
            collection: collHandle,
            look: nm ? (nm.textContent || '').trim() : '',
            href: card.getAttribute('href') || ''
          });
        }
      }, true);

      sync();
    });
  }

  /* ========================================================================
     EON — "Complete the look", styled by EON. PDP band that asks EON's public
     /api/storefront/style for complementary in-stock pieces + an Opus styling
     line, then reveals itself ONLY if a non-empty look comes back. Fails open
     (stays hidden) on any error — never an empty band. Base + shop + product
     ride on data-attrs so it's independent of the telemetry beacon.
     ===================================================================== */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  // Shopify CDN resize: keep recommended/editorial imagery light on mobile.
  // Never double-append — Liquid-built URLs may already carry a width.
  function eonImg(url, w) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + (w || 480);
  }
  // HONESTY/display fix (vet 2026-07-02): some legacy uploads (WhatsApp/iPhone
  // files with the wrong bytes behind a .jpg name) 404 on the CDN width
  // transform and some won't decode in the browser AT ALL — this was the
  // "broken product image inside a look" finding. Retry ONCE without the
  // transform; if the original doesn't decode either, hide the broken glyph
  // and let the tile fall back to clean paper.
  document.addEventListener('error', function (ev) {
    var img = ev.target;
    if (!img || img.tagName !== 'IMG') return;
    var src = img.getAttribute('src') || '';
    if (src.indexOf('cdn.shopify.com') === -1) return;
    if (!img.__cxRetried && /[?&]width=\d+/.test(src)) {
      img.__cxRetried = 1;
      img.src = src.replace(/[?&]width=\d+/, '').replace(/\?&/, '?');
      return;
    }
    if (!img.__cxDead) {
      img.__cxDead = 1;
      img.style.display = 'none';
      var host = img.closest && img.closest('.cx-eon__tile, .cx-eon__rowmedia, .cx-eon__framemedia, .cx-eon__media, .cx-cartrec__media, .cx-ask__im, .cx-ask__card');
      if (host) host.classList.add('is-noimg');
    }
  }, true);
  // Polite screen-reader announcer (one shared visually-hidden live region) so
  // AT users hear add-to-cart success/failure on this core conversion action.
  function eonLive(msg) {
    var r = document.getElementById('eon-live');
    if (!r) {
      r = document.createElement('div');
      r.id = 'eon-live'; r.setAttribute('role', 'status'); r.setAttribute('aria-live', 'polite');
      r.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;';
      document.body.appendChild(r);
    }
    r.textContent = '';
    setTimeout(function () { r.textContent = msg; }, 50);
  }
  // Updates the bare count bubble + publishes cartUpdate. Used as a fallback
  // when no cart drawer is on the page (e.g. the cart-page layout).
  function eonCartCountRefresh() {
    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
      try {
        var sel = '.cart-count-bubble, .cart-count, #cart-icon-bubble .count, [data-cart-count]';
        document.querySelectorAll(sel).forEach(function (n) { n.textContent = c.item_count; });
      } catch (e) {}
      try {
        if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'eon-stylist', cartData: c });
        }
      } catch (e) {}
    }).catch(function () {});
  }
  // Re-renders AND OPENS the Zenith cart drawer after an EON add. The plain
  // /cart/add.js POST carries no section HTML, and CartDrawer never subscribes
  // to cartUpdate, so without this the drawer never opens — the add reads as a
  // no-op (kills the "Add the look" AOV lever). Fetch the drawer + icon-bubble
  // section HTML and hand it to cartDrawer.renderContents() (which opens +
  // re-renders). Falls back to the count bubble when there is no drawer.
  function eonCartRefresh() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') {
      eonCartCountRefresh();
      return;
    }
    fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (sections) {
        if (!sections) { eonCartCountRefresh(); return; }
        // renderContents reads parsed.sections[id] for each id it tracks, then
        // calls drawer.open(); shape the payload it expects.
        drawer.renderContents({ sections: sections });
      })
      .catch(function () { eonCartCountRefresh(); });
  }
  // Safe AJAX add-to-cart. Never throws into the page; refreshes count + notifies
  // the cart. Surfaces Shopify's error reason; if an ATOMIC multi-add (items[])
  // fails because one piece went OOS, falls back to adding each piece singly so
  // the look still partly adds instead of all-or-nothing.
  function eonAddToCart(body, onState) {
    if (onState) onState('adding');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, status: r.status, j: j }; },
                             function () { return { ok: r.ok, status: r.status, j: null }; });
      })
      .then(function (res) {
        if (res.ok) {
          eonCartRefresh();
          if (onState) onState('added');
          if (window.EON && window.EON.track) window.EON.track('eon_look_add_to_cart', body);
          return;
        }
        if (body.items && body.items.length > 1) {
          var added = 0, n = body.items.length;
          body.items.forEach(function (it) {
            fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: it.id, quantity: it.quantity || 1 }) })
              .then(function (r) { if (r.ok) added++; }, function () {})
              .then(function () {
                if (--n === 0) {
                  if (added) eonCartRefresh();
                  if (onState) onState(added ? 'added' : 'error', added ? null : 'Some pieces sold out');
                }
              });
          });
          return;
        }
        if (onState) onState('error', (res.j && (res.j.description || res.j.message)) || (res.status === 422 ? 'Sold out' : 'Try again in a moment'));
      })
      .catch(function () { if (onState) onState('error', 'Network error'); });
  }
  function eonBtnState(btn, label) {
    return function (state, reason) {
      if (!btn) return;
      if (state === 'adding') { btn.disabled = true; btn.textContent = 'Adding…'; }
      else if (state === 'added') { btn.disabled = false; btn.textContent = 'Added'; eonLive('Added to cart'); setTimeout(function () { btn.textContent = label; }, 1800); }
      else if (state === 'error') {
        btn.disabled = false;
        btn.textContent = (reason && reason.length <= 16) ? reason : 'Try again';
        eonLive((reason || 'Could not add') + ', try again');
        setTimeout(function () { btn.textContent = label; }, 2600);
      }
    };
  }
  function eonTrack(ev, payload) {
    try { if (window.EON && window.EON.track) window.EON.track(ev, payload || {}); }
    catch (e) {}
  }
  function eonNgn(n) { return '₦' + Math.round(n || 0).toLocaleString('en-NG'); }

  // The shared "view the look" drawer — one element reused for every look. Lists the
  // look's pieces (image, name, price, per-piece Add) + an "Add all to cart" footer.
  // Built lazily on first open, appended to <body>, closed on backdrop/esc/×.
  var eonLookDrawer = null;
  function eonEnsureLookDrawer() {
    if (eonLookDrawer) return eonLookDrawer;
    var d = document.createElement('div');
    d.className = 'cx-look-drawer'; d.id = 'eon-look-drawer'; d.hidden = true;
    d.setAttribute('role', 'dialog'); d.setAttribute('aria-modal', 'true');
    d.setAttribute('aria-label', 'Shop this look');
    d.innerHTML =
      '<div class="cx-look-drawer__scrim" data-close></div>' +
      '<aside class="cx-look-drawer__panel" tabindex="-1">' +
        '<header class="cx-look-drawer__head">' +
          '<span class="cx-look-drawer__eyebrow">Shop this look</span>' +
          '<button type="button" class="cx-look-drawer__close" data-close aria-label="Close">&times;</button>' +
        '</header>' +
        '<div class="cx-look-drawer__hero"><img alt="" data-hero></div>' +
        '<p class="cx-look-drawer__line" data-line></p>' +
        '<ul class="cx-look-drawer__pieces" data-pieces></ul>' +
        '<div class="cx-look-drawer__foot" data-foot></div>' +
      '</aside>';
    document.body.appendChild(d);
    function close() {
      d.hidden = true; d.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      if (d.__opener && d.__opener.focus) { try { d.__opener.focus(); } catch (e) {} }
    }
    d.addEventListener('click', function (ev) {
      if (ev.target.closest && ev.target.closest('[data-close]')) { ev.preventDefault(); close(); }
    });
    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' && !d.hidden) close();
    });
    d.__close = close;
    eonLookDrawer = d;
    return d;
  }

  // Populate + open the drawer for one curated look. `look` is a looks-for-product
  // entry: {look_id,image_url,rationale,occasion,bundle_total,pieces:[{title,image_url,
  // price,variant_id,in_stock,url,handle,shopify_id,is_anchor}]}.
  function eonOpenLook(look, ctx, opener) {
    var d = eonEnsureLookDrawer();
    d.__opener = opener || null;
    // operator 2026-07-06: after a successful add the tray must get out of the
    // way — flash "Added", then close so the cart drawer isn't stacked under it
    function closeOnAdded(base) {
      return function (state, msg) {
        base(state, msg);
        if (state === 'added') { window.setTimeout(function () { if (d.__close) d.__close(); }, 450); }
      };
    }
    var hero = d.querySelector('[data-hero]');
    if (hero) { hero.src = eonImg(look.image_url || '', 720); hero.alt = look.occasion ? ('Look — ' + look.occasion) : 'Styled look'; }
    var line = d.querySelector('[data-line]');
    if (line) { line.textContent = look.rationale || ''; line.style.display = look.rationale ? '' : 'none'; }
    var pieces = look.pieces || [];
    var ul = d.querySelector('[data-pieces]'), html = '', inStock = [], total = 0;
    pieces.forEach(function (p) {
      var href = p.url || (p.handle ? '/products/' + encodeURIComponent(p.handle) : '#');
      var price = (p.price != null) ? eonNgn(p.price) : '';
      var buyable = p.in_stock && p.variant_id;
      if (buyable) { inStock.push(p.variant_id); total += (p.price || 0); }
      html += '<li class="cx-look-piece' + (p.is_anchor ? ' is-anchor' : '') + '">' +
        '<a class="cx-look-piece__media" href="' + esc(href) + '">' +
          (p.image_url ? '<img src="' + esc(eonImg(p.image_url, 200)) + '" alt="' + esc(p.title || '') + '" loading="lazy" width="84" height="105">' : '') +
        '</a>' +
        '<div class="cx-look-piece__info">' +
          '<a class="cx-look-piece__name" href="' + esc(href) + '">' + esc(p.title || 'Piece') + '</a>' +
          '<span class="cx-look-piece__price">' + price + '</span>' +
        '</div>' +
        (buyable
          ? '<button type="button" class="cx-look-piece__add" data-variant="' + esc(p.variant_id) + '" data-pid="' + esc(p.shopify_id || '') + '" aria-label="Add ' + esc(p.title || 'piece') + ' to cart">Add</button>'
          : '<span class="cx-look-piece__oos">Sold out</span>') +
        '</li>';
    });
    if (ul) {
      ul.innerHTML = html;
      ul.onclick = function (ev) {
        var add = ev.target.closest && ev.target.closest('.cx-look-piece__add');
        if (!add) return;
        ev.preventDefault();
        eonAddToCart({ id: add.getAttribute('data-variant'), quantity: 1 }, closeOnAdded(eonBtnState(add, 'Add')));
        eonTrack('eon_look_click', { look_id: look.look_id, source_product_id: ctx.pid,
          product_shopify_id: add.getAttribute('data-pid') || '' });
      };
    }
    var foot = d.querySelector('[data-foot]');
    if (foot) {
      if (inStock.length >= 2) {
        var label = 'Add the look — ' + eonNgn(total);
        foot.innerHTML = '<button type="button" class="cx-look-drawer__addall" aria-label="' + esc(label) + ' — adds ' + inStock.length + ' pieces">' + esc(label) + '</button>';
        var ab = foot.querySelector('.cx-look-drawer__addall');
        ab.onclick = function () {
          eonAddToCart({ items: inStock.map(function (v) { return { id: v, quantity: 1 }; }) }, closeOnAdded(eonBtnState(ab, label)));
          eonTrack('eon_look_click', { look_id: look.look_id, source_product_id: ctx.pid, action: 'add_all' });
        };
      } else { foot.innerHTML = ''; }
    }
    d.hidden = false;
    requestAnimationFrame(function () { d.classList.add('is-open'); });
    document.documentElement.style.overflow = 'hidden';
    var panel = d.querySelector('.cx-look-drawer__panel');
    if (panel && panel.focus) { try { panel.focus(); } catch (e) {} }
    eonTrack('eon_look_click', { look_id: look.look_id, source_product_id: ctx.pid, action: 'open' });
  }

  // ── hotspot pin popover — a single shared mini product-card, anchored to the
  // tapped pin on a modeled look image (Daniel 2026-06-30: "a hotspot tag on products
  // used in ways to wear it"). Tap a dot → that product's card + Add.
  var eonPinPop = null;
  function eonEnsurePinPop() {
    if (eonPinPop) return eonPinPop;
    var p = document.createElement('div');
    p.className = 'cx-eon-pinpop'; p.hidden = true;
    document.body.appendChild(p);
    document.addEventListener('click', function (ev) {
      if (p.hidden || !ev.target.closest) return;
      if (ev.target.closest('.cx-eon-pinpop') || ev.target.closest('.cx-eon__pin')) return;
      p.hidden = true;
    });
    document.addEventListener('keydown', function (ev) { if (ev.key === 'Escape') p.hidden = true; });
    eonPinPop = p; return p;
  }
  function eonOpenPin(pinBtn, pin, ctx) {
    var p = eonEnsurePinPop();
    var href = pin.url || (pin.handle ? '/products/' + encodeURIComponent(pin.handle) : '#');
    var price = (pin.price != null) ? eonNgn(pin.price) : '';
    var buyable = pin.in_stock && pin.variant_id;
    p.innerHTML =
      '<a class="cx-eon-pinpop__media" href="' + esc(href) + '">' +
        (pin.image_url ? '<img src="' + esc(eonImg(pin.image_url, 160)) + '" alt="' + esc(pin.title || '') + '" width="60" height="75">' : '') +
      '</a>' +
      '<div class="cx-eon-pinpop__info">' +
        '<a class="cx-eon-pinpop__name" href="' + esc(href) + '">' + esc(pin.title || 'Piece') + '</a>' +
        '<span class="cx-eon-pinpop__price">' + price + '</span>' +
        (buyable
          ? '<button type="button" class="cx-eon-pinpop__add" data-variant="' + esc(pin.variant_id) + '">Add</button>'
          : '<span class="cx-eon-pinpop__oos">Sold out</span>') +
      '</div>';
    p.hidden = false;
    var r = pinBtn.getBoundingClientRect();
    var pw = p.offsetWidth || 220, ph = p.offsetHeight || 96;
    var left = window.scrollX + r.left + r.width / 2 - pw / 2;
    left = Math.max(8, Math.min(left, window.scrollX + document.documentElement.clientWidth - pw - 8));
    var top = window.scrollY + r.top - ph - 10;
    if (top < window.scrollY + 8) top = window.scrollY + r.bottom + 10;
    p.style.left = left + 'px'; p.style.top = top + 'px';
    var add = p.querySelector('.cx-eon-pinpop__add');
    if (add) add.onclick = function () {
      eonAddToCart({ id: add.getAttribute('data-variant'), quantity: 1 }, eonBtnState(add, 'Add'));
      eonTrack('eon_look_click', { look_id: ctx.lookId, source_product_id: ctx.pid, product_shopify_id: pin.shopify_id || '', action: 'pin_add' });
    };
    eonTrack('eon_look_click', { look_id: ctx.lookId, source_product_id: ctx.pid, product_shopify_id: pin.shopify_id || '', action: 'pin_open' });
  }

  // One normalized product card (Mode B/C share this markup) → an in-stock variant Adds,
  // an out-of-stock piece still links to the PDP.
  function eonCardItemHTML(it) {
    var href = it.handle ? ('/products/' + encodeURIComponent(it.handle)) : (it.url || '#');
    var price = (it.unit != null) ? eonNgn(it.unit) : '';
    return '<div class="cx-eon__card" data-handle="' + esc(it.handle || '') + '" data-pid="' + esc(it.pid || '') + '">' +
      '<a class="cx-eon__link" href="' + esc(href) + '">' +
        '<div class="cx-eon__media">' + (it.image_url ? '<img src="' + esc(eonImg(it.image_url, 480)) + '" alt="' + esc(it.title || '') + '" loading="lazy" width="320" height="400">' : '') + '</div>' +
        '<div class="cx-eon__info"><span class="cx-eon__name">' + esc(it.title || '') + '</span>' +
        (price ? '<span class="cx-eon__price">' + price + '</span>' : '') + '</div>' +
      '</a>' +
      (it.variant_id ? '<button type="button" class="cx-eon__add" data-variant="' + esc(it.variant_id) + '" aria-label="Add ' + esc(it.title || 'piece') + ' to cart">Add</button>' : '') +
      '</div>';
  }

  // Mount a product-card style into the grid (Mode B: an approved-but-unmodeled look's
  // pieces; Mode C: the algorithmic /style look). ONE style — never the raw look photo.
  function eonMountCardGrid(el, pid, items, opts) {
    opts = opts || {};
    var grid = el.querySelector('[data-eon-grid]');
    if (!grid || !items.length) return false;
    var inStock = [], total = 0;
    items.forEach(function (it) { if (it.variant_id) { inStock.push(it.variant_id); total += (it.unit || 0); } });
    grid.innerHTML = items.map(eonCardItemHTML).join('');
    grid.onclick = function (ev) {
      var addBtn = ev.target.closest && ev.target.closest('.cx-eon__add');
      if (addBtn) { ev.preventDefault(); eonAddToCart({ id: addBtn.getAttribute('data-variant'), quantity: 1 }, eonBtnState(addBtn, 'Add')); return; }
      var card = ev.target.closest && ev.target.closest('.cx-eon__card');
      if (card) eonTrack('eon_look_click', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'cards', product_shopify_id: card.getAttribute('data-pid') || '', product_handle: card.getAttribute('data-handle') || '' });
    };
    var line = el.querySelector('[data-eon-rationale]');
    if (line) { if (opts.rationale) { line.textContent = opts.rationale; } else if (line.parentNode) { line.style.display = 'none'; } }
    if (inStock.length >= 2) {
      var foot = el.querySelector('[data-eon-foot]');
      if (foot) {
        var label = 'Add the look — ' + eonNgn(total);
        foot.innerHTML = '<button type="button" class="cx-eon__addlook" aria-label="' + esc(label) + ' — adds ' + inStock.length + ' pieces to cart">' + esc(label) + '</button>';
        var lbtn = foot.querySelector('.cx-eon__addlook');
        lbtn.onclick = function () {
          eonAddToCart({ items: inStock.map(function (v) { return { id: v, quantity: 1 }; }) }, eonBtnState(lbtn, label));
          eonTrack('eon_look_click', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'cards', action: 'add_all' });
        };
      }
    }
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    eonTrack('eon_look_served', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'cards' });
    return true;
  }

  // Re-project hotspot pins for a cover-cropped image. Pin x/y are fractions of
  // the FULL image; object-fit:cover crops it, so raw percentages drift off the
  // garment. Once the image loads, project pin coords into container space and
  // hide any pin that falls outside the visible crop. Re-runs on resize.
  function eonFixCoverPins(scope) {
    $all('img[data-cover-pins]', scope).forEach(function (img) {
      function fix() {
        var box = img.parentNode;
        if (!box) return;
        var cw = box.clientWidth, ch = box.clientHeight;
        var nw = img.naturalWidth, nh = img.naturalHeight;
        if (!cw || !ch || !nw || !nh) return;
        var scale = Math.max(cw / nw, ch / nh);
        var dw = nw * scale, dh = nh * scale;
        var ox = (dw - cw) / 2, oy = (dh - ch) / 2;
        $all('.cx-eon__pin', box).forEach(function (pin) {
          var fx = parseFloat(pin.getAttribute('data-x')), fy = parseFloat(pin.getAttribute('data-y'));
          if (isNaN(fx) || isNaN(fy)) return;
          var px = fx * dw - ox, py = fy * dh - oy;
          if (px < 0 || px > cw || py < 0 || py > ch) { pin.hidden = true; return; }
          pin.hidden = false;
          pin.style.left = (px / cw * 100).toFixed(2) + '%';
          pin.style.top = (py / ch * 100).toFixed(2) + '%';
        });
      }
      if (img.complete && img.naturalWidth) fix();
      else on(img, 'load', fix);
      on(window, 'resize', fix, { passive: true });
    });
  }

  // ── CTL·A — "The Stylist's Note" (operator-picked 2026-07-02). The product
  // being viewed leads a mosaic, EON's rationale reads as a stylist's note with
  // its receipts line, companions sit in item rows (live size select + REAL
  // scarcity + per-piece add), and one CTA bar adds the whole look. Sizes
  // hydrate lazily from /products/{handle}.js (single-option products only);
  // scarcity comes from /api/storefront/social-proof. Both fail open — the row
  // still adds EON's default in-stock variant.
  function eonMountStylistNote(el, pid, items, opts) {
    opts = opts || {};
    var mount = el.querySelector('[data-eon-note]');
    if (!mount || !items.length) return false;
    var anchorImg = el.getAttribute('data-anchor-image') || (opts.anchor && opts.anchor.image_url) || '';
    var anchorTitle = el.getAttribute('data-anchor-title') || (opts.anchor && opts.anchor.title) || '';
    var tiles = '';
    var sideN = Math.max(1, Math.min(items.length, 3));
    if (anchorImg) {
      tiles += '<div class="cx-eon__tile cx-eon__tile--hero" style="grid-row:1 / span ' + sideN + '"><span class="cx-eon__tileidx">01 · This piece</span><img src="' + esc(eonImg(anchorImg, 760)) + '" alt="' + esc(anchorTitle) + '" loading="lazy" width="380" height="500"></div>';
    }
    items.slice(0, 3).forEach(function (it, i) {
      tiles += '<div class="cx-eon__tile"><span class="cx-eon__tileidx">' + pad2(i + 2) + '</span>' +
        (it.image_url ? '<img src="' + esc(eonImg(it.image_url, 460)) + '" alt="' + esc(it.title || '') + '" loading="lazy" width="230" height="230">' : '') +
        '<span class="cx-eon__tilename">' + esc(it.title || '') + '</span></div>';
    });
    var rows = '';
    items.forEach(function (it, i) {
      var href = it.handle ? ('/products/' + encodeURIComponent(it.handle)) : (it.url || '#');
      rows += '<div class="cx-eon__row" data-row data-pid="' + esc(it.pid || '') + '" data-handle="' + esc(it.handle || '') + '"' + (it.variant_id ? ' data-variant="' + esc(it.variant_id) + '"' : '') + ' data-unit="' + (it.unit || 0) + '">' +
        '<a class="cx-eon__rowmedia" href="' + esc(href) + '">' + (it.image_url ? '<img src="' + esc(eonImg(it.image_url, 160)) + '" alt="" loading="lazy" width="56" height="70">' : '') + '</a>' +
        '<div class="cx-eon__rowinfo">' +
          '<a class="cx-eon__rowname" href="' + esc(href) + '">' + esc(it.title || 'Piece') + '</a>' +
          '<span class="cx-eon__rowsub"><span>' + pad2(i + 2) + '</span>' + (it.unit != null ? ' · ' + eonNgn(it.unit) : '') + '<em data-row-proof></em></span>' +
        '</div>' +
        '<select class="cx-eon__rowsize" data-row-size hidden aria-label="Size for ' + esc(it.title || 'piece') + '"></select>' +
        (it.variant_id
          ? '<button type="button" class="cx-eon__rowadd" data-row-add aria-label="Add ' + esc(it.title || 'piece') + ' to cart">+</button>'
          : '<span class="cx-eon__rowoos">Sold out</span>') +
        '</div>';
    });
    var inStock = items.filter(function (it) { return it.variant_id; });
    var total = 0;
    inStock.forEach(function (it) { total += (it.unit || 0); });
    var note = opts.rationale
      ? ('<blockquote class="cx-eon__quote">&ldquo;' + esc(opts.rationale) + '&rdquo;' +
         '<span class="cx-eon__quotewho">EON &middot; styled for this piece</span></blockquote>')
      : '';
    mount.innerHTML =
      '<div class="cx-eon__mosaic">' + tiles + '</div>' +
      '<div class="cx-eon__notecol">' + note +
        '<div class="cx-eon__rows">' + rows + '</div>' +
        (inStock.length >= 2
          ? '<button type="button" class="cx-eon__ctabar" data-note-cta><span data-note-cta-label>Add the look &middot; ' + inStock.length + ' pieces</span><span class="cx-eon__ctatot" data-note-cta-total>' + eonNgn(total) + '</span></button>'
          : '') +
        '<div class="cx-eon__microrow"><span>One delivery</span><span>Sizes checked live</span></div>' +
      '</div>';
    mount.hidden = false;
    // the note carries the rationale — hide the head line so it never doubles
    var headLine = el.querySelector('[data-eon-rationale]');
    if (headLine) headLine.style.display = 'none';

    function rowVariant(row) { return row.getAttribute('data-variant'); }
    function recompute() {
      var t = 0, n = 0;
      $all('[data-row]', mount).forEach(function (row) {
        if (!rowVariant(row)) return;
        n++; t += parseFloat(row.getAttribute('data-unit')) || 0;
      });
      var lab = mount.querySelector('[data-note-cta-label]');
      var tot = mount.querySelector('[data-note-cta-total]');
      if (lab) lab.innerHTML = 'Add the look &middot; ' + n + ' piece' + (n === 1 ? '' : 's');
      if (tot) tot.textContent = eonNgn(t);
    }
    mount.addEventListener('click', function (ev) {
      var add = ev.target.closest && ev.target.closest('[data-row-add]');
      if (add) {
        ev.preventDefault();
        var row = add.closest('[data-row]');
        var vid = row && rowVariant(row);
        if (vid) {
          eonAddToCart({ id: vid, quantity: 1 }, eonBtnState(add, '+'));
          eonTrack('eon_look_click', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'note', product_shopify_id: row.getAttribute('data-pid') || '' });
        }
        return;
      }
      var cta = ev.target.closest && ev.target.closest('[data-note-cta]');
      if (cta) {
        var ids = [];
        $all('[data-row]', mount).forEach(function (row) { var v = rowVariant(row); if (v) ids.push(v); });
        if (!ids.length) return;
        eonAddToCart({ items: ids.map(function (v) { return { id: v, quantity: 1 }; }) },
          eonBtnState(cta.querySelector('[data-note-cta-label]') || cta, 'Add the look · ' + ids.length + ' pieces'));
        eonTrack('eon_look_click', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'note', action: 'add_all' });
        return;
      }
      var rowLink = ev.target.closest && ev.target.closest('.cx-eon__rowname, .cx-eon__rowmedia');
      if (rowLink) {
        var r = rowLink.closest('[data-row]');
        if (r) eonTrack('eon_look_click', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'note', product_shopify_id: r.getAttribute('data-pid') || '', product_handle: r.getAttribute('data-handle') || '' });
      }
    });
    // live sizes — a single-option product's in-stock sizes become a row select
    $all('[data-row]', mount).forEach(function (row) {
      var handle = row.getAttribute('data-handle');
      var sel = row.querySelector('[data-row-size]');
      if (!handle || !sel || !rowVariant(row)) return;
      fetch('/products/' + encodeURIComponent(handle) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants || !p.options || p.options.length !== 1) return;
          var avail = p.variants.filter(function (v) { return v.available; });
          if (avail.length < 2) return;
          sel.innerHTML = avail.map(function (v) {
            return '<option value="' + esc(v.id) + '" data-price="' + (v.price / 100) + '"' +
              (String(v.id) === String(rowVariant(row)) ? ' selected' : '') + '>' + esc(v.option1) + '</option>';
          }).join('');
          sel.hidden = false;
          sel.addEventListener('change', function () {
            var opt = sel.options[sel.selectedIndex];
            if (!opt) return;
            row.setAttribute('data-variant', opt.value);
            if (opt.getAttribute('data-price')) row.setAttribute('data-unit', opt.getAttribute('data-price'));
            recompute();
          });
        })
        .catch(function () { /* fail open — default variant still adds */ });
    });
    // REAL scarcity on the rows (never invented) — social-proof badge text
    var C = window.EON_STOREFRONT;
    if (C && C.enabled && C.base) {
      var proofIds = items.map(function (it) { return it.pid; }).filter(Boolean);
      if (proofIds.length) {
        fetch(String(C.base).replace(/\/+$/, '') + '/api/storefront/social-proof?shop=' + encodeURIComponent(C.shop || '') + '&product_ids=' + encodeURIComponent(proofIds.join(',')), { mode: 'cors' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            if (!d || !d.ok || !d.items) return;
            $all('[data-row]', mount).forEach(function (row) {
              var it = d.items[row.getAttribute('data-pid')];
              var em = row.querySelector('[data-row-proof]');
              if (it && it.badge && em) {
                em.textContent = ' · ' + it.badge;
                if (it.low_stock != null) em.className = 'is-scarce';
              }
            });
          }).catch(function () { /* fail open — no scarcity line */ });
      }
    }
    el.hidden = false;
    requestAnimationFrame(function () { el.classList.add('is-in'); });
    eonTrack('eon_look_served', { look_id: opts.lookId || '', source_product_id: pid, mode: opts.mode || 'note', cached: !!opts.cached });
    return true;
  }

  // CURATED-FIRST renderer (Daniel 2026-06-30 structure):
  //   MODE A — modeled looks (a studio render on a model) → "Ways to wear it" IMAGE
  //            carousel + tappable hotspot pins on each product.
  //   MODE B — no modeled look but an approved look exists → ONE clean product-card
  //            style from its pieces (never the tacky un-modeled photo).
  // Returns true if it rendered (caller skips the algorithmic fallback), false otherwise.
  function eonRenderCuratedLooks(el, base, shop, pid, looksLimit, done) {
    var url = base + '/api/storefront/looks-for-product?shop=' + encodeURIComponent(shop) +
      '&product_id=' + encodeURIComponent(pid) + '&limit=' + encodeURIComponent(looksLimit);
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var to = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, 9000) : null;
    fetch(url, { mode: 'cors', signal: ctrl ? ctrl.signal : undefined })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        if (to) { clearTimeout(to); to = null; }
        var all = (d && d.ok && d.looks) ? d.looks : [];
        var modeled = all.filter(function (l) { return l.modeled && l.image_url && (l.pieces || []).length; });
        var ctx = { pid: pid }, byId = {};

        // ── MODE A: WTS·A "The Filmstrip" (operator-picked 2026-07-02) —
        //    full-bleed look frames in a snap strip, caption gradient carrying
        //    occasion + pieces + real bundle total, "Look NN" chip, hotspot pins
        //    re-projected for the cover crop. Tap a frame → the shared look
        //    drawer; tap a pin → the pin popover. Same data, same drawer.
        if (modeled.length) {
          var wrap = el.querySelector('[data-eon-looks]');
          if (wrap) {
            var titleEl = el.querySelector('[data-eon-title]');
            if (titleEl) {
              titleEl.textContent = (modeled.length >= 2)
                ? (modeled.length + ' ways to wear it')
                : (el.getAttribute('data-curated-heading') || 'Ways to wear it');
            }
            var html = '';
            modeled.forEach(function (l, idx) {
              byId[l.look_id] = l;
              var n = (typeof l.in_stock_count === 'number') ? l.in_stock_count : (l.pieces || []).length;
              var priceTxt = (l.bundle_total != null) ? eonNgn(l.bundle_total) : '';
              var pins = '';
              (l.pins || []).forEach(function (pin, i) {
                pins += '<button type="button" class="cx-eon__pin" data-x="' + (+pin.x || 0) + '" data-y="' + (+pin.y || 0) + '" style="left:' + (pin.x * 100).toFixed(2) + '%;top:' + (pin.y * 100).toFixed(2) + '%" data-look-id="' + esc(l.look_id) + '" data-pin="' + i + '" aria-label="' + esc((pin.title || 'Product') + (pin.price != null ? ' — ' + eonNgn(pin.price) : '')) + '"><span class="cx-eon__pindot"></span></button>';
              });
              var subBits = [n + ' piece' + (n === 1 ? '' : 's')];
              if (priceTxt) subBits.push(priceTxt);
              subBits.push('tap to shop');
              html += '<div class="cx-eon__frame" role="listitem">' +
                '<div class="cx-eon__framemedia" data-open-look="' + esc(l.look_id) + '" role="button" tabindex="0" aria-label="View this look' + (priceTxt ? ' — ' + esc(priceTxt) : '') + '">' +
                  '<img src="' + esc(eonImg(l.image_url, 720)) + '" alt="' + esc(l.occasion || 'Styled look') + '" loading="lazy" width="380" height="500" data-cover-pins>' +
                  pins +
                  '<span class="cx-eon__framenum">Look ' + pad2(idx + 1) + '</span>' +
                  '<div class="cx-eon__framecap">' +
                    '<span class="cx-eon__framelk">' + esc(l.occasion || 'The look') + '</span>' +
                    '<span class="cx-eon__framesub">' + esc(subBits.join(' · ')) + '</span>' +
                  '</div>' +
                '</div>' +
              '</div>';
            });
            // "See all looks" tail frame — only when the operator set a target URL
            var seeAll = el.getAttribute('data-see-all');
            if (seeAll) {
              html += '<a class="cx-eon__frame cx-eon__frame--more" href="' + esc(seeAll) + '">See all<br>looks <span aria-hidden="true">&rarr;</span></a>';
            }
            wrap.innerHTML = html;
            wrap.setAttribute('role', 'list');
            wrap.hidden = false;
            eonFixCoverPins(wrap);
            function openFromEl(t) { var id = t.getAttribute('data-open-look'); var l = byId[id]; if (l) eonOpenLook(l, ctx, t); }
            wrap.addEventListener('click', function (ev) {
              var pinBtn = ev.target.closest && ev.target.closest('.cx-eon__pin');
              if (pinBtn) { ev.preventDefault(); ev.stopPropagation(); var l = byId[pinBtn.getAttribute('data-look-id')]; var pin = l && (l.pins || [])[+pinBtn.getAttribute('data-pin')]; if (pin) eonOpenPin(pinBtn, pin, { pid: pid, lookId: l.look_id }); return; }
              var opener = ev.target.closest && ev.target.closest('[data-open-look]');
              if (opener) openFromEl(opener);
            });
            wrap.addEventListener('keydown', function (ev) {
              if (ev.key !== 'Enter' && ev.key !== ' ') return;
              var opener = ev.target.closest && ev.target.closest('.cx-eon__lookmedia[data-open-look]');
              if (opener) { ev.preventDefault(); openFromEl(opener); }
            });
            el.hidden = false;
            requestAnimationFrame(function () { el.classList.add('is-in'); });
            modeled.forEach(function (l) { eonTrack('eon_look_served', { look_id: l.look_id, source_product_id: pid, curated: true, mode: 'modeled' }); });
            done(true); return;
          }
        }

        // ── MODE B: CTL·A "Stylist's Note" from the best approved (unmodeled)
        //    look — the anchor leads the mosaic, companions fill the rows.
        var card = all.filter(function (l) { return !l.modeled && (l.pieces || []).length; })[0];
        if (card) {
          var anchorPiece = (card.pieces || []).filter(function (p) { return p.is_anchor; })[0];
          var items = (card.pieces || []).filter(function (p) { return !p.is_anchor; }).map(function (p) {
            return { handle: p.handle, pid: p.shopify_id, title: p.title, image_url: p.image_url,
                     variant_id: (p.in_stock ? p.variant_id : null), unit: p.price, url: p.url };
          });
          if (!items.length) {
            // look was only the anchor itself — nothing to complete with
            done(false); return;
          }
          if (eonMountStylistNote(el, pid, items, { lookId: card.look_id, rationale: card.rationale, mode: 'note-curated', anchor: anchorPiece })) { done(true); return; }
        }
        done(false);
      })
      .catch(function () { if (to) { clearTimeout(to); to = null; } done(false); });
  }

  function initEonStylist(root) {
    var els = (root || document).querySelectorAll('[data-eon-stylist]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.__eonBound) return; el.__eonBound = true;
      var base = (el.getAttribute('data-eon-base') || '').replace(/\/+$/, '');
      var shop = el.getAttribute('data-eon-shop') || '';
      var pid = el.getAttribute('data-product-id') || '';
      var limit = el.getAttribute('data-limit') || '4';
      var looksLimit = el.getAttribute('data-looks-limit') || '4';
      if (!base || !pid) return;
      // CURATED-FIRST: try the real studio looks; only fall back to the algorithmic
      // /style band when the product has no curated look yet.
      eonRenderCuratedLooks(el, base, shop, pid, looksLimit, function (rendered) {
        if (!rendered) loadAlgorithmic(0);
      });
      var url = base + '/api/storefront/style?shop=' + encodeURIComponent(shop) +
        '&product_id=' + encodeURIComponent(pid) + '&limit=' + encodeURIComponent(limit);
      // A cold-cache FIRST view computes the look synchronously (Opus), which can
      // outrun a tight timeout — leaving the band hidden until a manual refresh
      // served it from the now-warm cache. Give the first call room, and retry
      // ONCE if it isn't ready, so the band appears on its own.
      function loadAlgorithmic(attempt) {
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var to = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, 16000) : null;
      fetch(url, { mode: 'cors', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (to) { clearTimeout(to); to = null; }
          if (!d || !d.ok || !d.look || !d.look.length) { if (attempt < 1) { setTimeout(function () { loadAlgorithmic(attempt + 1); }, 5000); } return; }
          // CTL·A — render the algorithmic look as the Stylist's Note. Price is
          // the variant ACTUALLY added (highest-stock), not price_min (cheapest),
          // so the CTA total never understates the charge.
          var items = d.look.map(function (p) {
            var unit = (p.variant_price != null) ? p.variant_price : p.price_min;
            return { handle: p.handle, pid: p.shopify_id, title: p.title, image_url: p.image_url,
                     variant_id: p.variant_id || null, unit: unit };
          });
          eonMountStylistNote(el, pid, items, { rationale: d.rationale, mode: 'note', cached: !!d.cached });
        })
        .catch(function () { if (to) { clearTimeout(to); to = null; } if (attempt < 1) { setTimeout(function () { loadAlgorithmic(attempt + 1); }, 5000); } /* else leave hidden — fail open */ });
      }
      // loadAlgorithmic(0) is invoked by the curated-first callback above only when
      // the product has no curated studio look — so the two never both render.
    });
  }

  /* ========================================================================
     EON social proof — honest demand badges on PLP cards ("Only N left",
     "Selling fast", "N sold this month") from /api/storefront/social-proof.
     Real truth from eon_sync, not a manufactured reviews widget. No-op unless
     EON is enabled. Batched, idempotent, fails open (no badge).
     ===================================================================== */
  function initSocialProof(root) {
    var C = window.EON_STOREFRONT;
    if (!C || !C.enabled || !C.base) return;
    var scope = root || document;
    var map = {}, ids = [];
    Array.prototype.forEach.call(scope.querySelectorAll('.cx-pcard[data-product-id], .cx-ribbon__card[data-product-id]'), function (a) {
      var id = a.getAttribute('data-product-id');
      var slot = a.querySelector('[data-cx-proof]');
      if (!id || !slot || slot.__eonProof) return;
      slot.__eonProof = true;
      if (!map[id]) { map[id] = []; ids.push(id); }
      map[id].push(slot);
    });
    if (!ids.length) return;
    var base = String(C.base).replace(/\/+$/, ''), shop = encodeURIComponent(C.shop || '');
    for (var i = 0; i < ids.length; i += 50) {
      (function (chunk) {
        fetch(base + '/api/storefront/social-proof?shop=' + shop +
              '&product_ids=' + encodeURIComponent(chunk.join(',')), { mode: 'cors' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (d) {
            if (!d || !d.ok || !d.items) return;
            chunk.forEach(function (id) {
              var it = d.items[id];
              if (!it || !it.badge) return;
              (map[id] || []).forEach(function (slot) {
                slot.textContent = it.badge;
                if (it.low_stock != null) {
                  slot.classList.add(slot.classList.contains('cx-ribbon__proof')
                    ? 'cx-ribbon__proof--scarce' : 'cx-pcard__proof--scarce');
                }
                slot.hidden = false;
                // surface to screen readers — the badge sits inside an
                // aria-labelled media link that would otherwise mask it
                var lk = slot.closest('a[aria-label]');
                if (lk && lk.getAttribute('aria-label').indexOf(it.badge) === -1) {
                  lk.setAttribute('aria-label', lk.getAttribute('aria-label') + ' — ' + it.badge);
                }
              });
            });
          }).catch(function () { /* fail open — no badge */ });
      })(ids.slice(i, i + 50));
    }
  }

  /* ========================================================================
     EON auto collection band — fills the PLP editorial band with this
     collection's own CLEAN product shots when no curated editorial exists.
     Only QC-passed (or unscanned) images come back from EON; flagged photos
     never show. The deck ships WITHOUT data-cx-fx (so initFxSlider skips the
     empty shell); once cards load we add data-cx-fx, reveal, and bind the
     slider. Removes the whole band on empty/failure — never an empty band.
     ===================================================================== */
  function initCollectionBand(root) {
    var C = window.EON_STOREFRONT;
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-cx-band-auto]'), function (deck) {
      if (deck.__bandBound) return; deck.__bandBound = true;
      var wrap = deck.closest('[data-cx-band-wrap]');
      function drop() { if (wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap); }
      if (!C || !C.enabled || !C.base) { drop(); return; }
      var handle = deck.getAttribute('data-cx-band-auto') || '';
      var limit = parseInt(deck.getAttribute('data-cx-band-limit'), 10) || 8;
      if (!handle) { drop(); return; }
      var base = String(C.base).replace(/\/+$/, ''), shop = encodeURIComponent(C.shop || '');
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var to = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, 9000) : null;
      fetch(base + '/api/storefront/band?shop=' + shop +
            '&collection=' + encodeURIComponent(handle) + '&limit=' + limit,
            { mode: 'cors', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (to) { clearTimeout(to); to = null; }
          var items = (d && d.ok && d.items) || [];
          if (!items.length) { drop(); return; }
          var track = deck.querySelector('[data-cx-fx-track]');
          if (!track) { drop(); return; }
          // image_url is already CDN-width-sized by EON; don't double-size
          track.innerHTML = items.map(function (p, i) {
            var href = p.handle ? ('/products/' + encodeURIComponent(p.handle)) : '#';
            var img = p.image_url ? '<img src="' + esc(p.image_url) + '" alt="' + esc(p.title) +
              '" loading="lazy" width="360" height="450">' : '';
            return '<a class="cx-fx__card' + (i === 0 ? ' is-active' : '') + '" href="' + href + '">' +
                '<div class="cx-fx__media">' +
                  '<span class="cx-fx__wish" aria-hidden="true">&#9825;</span>' + img +
                '</div>' +
                '<div class="cx-fx__body">' +
                  '<span class="cx-fx__eyebrow">CRUX</span>' +
                  '<span class="cx-fx__name">' + esc(p.title) + '</span>' +
                  '<span class="cx-fx__shop">Shop ' + esc(p.title) + ' <span class="ar">&rarr;</span></span>' +
                '</div>' +
              '</a>';
          }).join('');
          // promote the now-filled deck into a live FX slider, then reveal it
          deck.setAttribute('data-cx-collection', handle);
          deck.setAttribute('data-cx-fx', '');
          deck.__cruxFxBound = false;
          if (wrap) wrap.hidden = false;
          initFxSlider(wrap || document);
          if (window.EON && window.EON.track) {
            window.EON.track('band_auto_served', { collection: handle, count: items.length });
          }
        })
        .catch(function () { if (to) { clearTimeout(to); to = null; } drop(); });
    });
  }

  /* ========================================================================
     EON verified-buyer reviews — hydrate the PDP star row from REAL reviews
     (customer_reviews / order_reviews, attributed per product) via
     /api/storefront/reviews. Reveals ONLY when EON has reviews for this product
     (count > 0) — never fakes a star. Fails open (stays hidden) on any error.
     ===================================================================== */
  function pdpStars(rating) {
    var full = Math.floor(rating), rem = rating - full;
    var half = rem >= 0.25 && rem < 0.75;
    if (rem >= 0.75) full += 1;
    var s = '';
    for (var i = 0; i < Math.min(full, 5); i++) s += '★';
    return '<span class="pdp__stars">' + s + (half ? '<span class="is-half">★</span>' : '') + '</span>';
  }
  function initProductReviews(root) {
    var C = window.EON_STOREFRONT;
    if (!C || !C.enabled || !C.base) return;
    var scope = root || document;
    var els = scope.querySelectorAll('[data-cx-reviews][data-product-id]');
    if (!els.length) return;
    var base = String(C.base).replace(/\/+$/, ''), shop = encodeURIComponent(C.shop || '');
    Array.prototype.forEach.call(els, function (el) {
      if (el.__cxRevBound) return; el.__cxRevBound = true;
      var pid = el.getAttribute('data-product-id'); if (!pid) return;
      fetch(base + '/api/storefront/reviews?shop=' + shop + '&product_ids=' + encodeURIComponent(pid),
            { mode: 'cors' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.ok || !d.items) return;
          var it = d.items[pid];
          if (!it || !it.count) return;            // never reveal a fake/empty rating
          var rating = it.rating || 0, disp = (Math.round(rating * 10) / 10).toFixed(1);
          el.innerHTML = pdpStars(rating) +
            '<span class="u-num">' + disp + '</span>' +
            '<span aria-hidden="true">&middot;</span>' +
            '<a class="c-link c-link--muted" href="#reviews">' + it.count +
            ' review' + (it.count === 1 ? '' : 's') + '</a>';
          el.hidden = false;
          el.setAttribute('aria-label', disp + ' out of 5 from ' + it.count +
            ' verified-buyer review' + (it.count === 1 ? '' : 's'));
          // avoid a duplicate row if the metafield path also rendered stars
          var sib = el.parentNode && el.parentNode.querySelector('.pdp__reviews:not([data-cx-reviews])');
          if (sib) sib.hidden = true;
          // optional: drop consented public snippets into a #reviews anchor if present
          if (it.reviews && it.reviews.length) {
            var box = document.getElementById('reviews');
            if (box && !box.__cxFilled) {
              box.__cxFilled = true;
              box.innerHTML = '<div class="cx-revlist">' + it.reviews.map(function (rv) {
                return '<blockquote class="cx-rev">' +
                  '<div class="cx-rev__stars" aria-hidden="true">' + pdpStars(rv.rating || 5) + '</div>' +
                  '<p class="cx-rev__text">' + esc(rv.text) + '</p>' +
                  '<cite class="cx-rev__by">' + esc(rv.name || 'Verified buyer') +
                  (rv.date ? ' &middot; ' + esc(rv.date) : '') + '</cite></blockquote>';
              }).join('') + '</div>';
            }
          }
        })
        .catch(function () { /* fail open — no stars */ });
    });
  }

  /* ========================================================================
     G6. MOBILE STICKY ATC  ([data-cx-satc])
        Reveals the sticky bottom bar once the main PDP ATC button scrolls
        out of view upward; hides when it scrolls back in. The compact
        <select> drives the bar price + the Add button's variant id; Add
        posts via eonAddToCart. Mobile-only is enforced in CSS (the bar is
        display:none above 749px), but we also no-op the observer on wide
        viewports so we never reveal it. Fail-open + reduced-motion safe.
     ===================================================================== */
  function initStickyATC(root) {
    var bars = $all('[data-cx-satc]', root || document);
    bars.forEach(function (bar) {
      if (bar.__cruxStickyBound) return; bar.__cruxStickyBound = true;

      var sel   = $('[data-cx-satc-select]', bar);
      var price = $('[data-cx-satc-price]', bar);
      var add   = $('[data-cx-satc-add]', bar);
      if (!add) return;

      // Find the main ATC button to observe — scope to the same product section
      // when possible, else fall back to the first one on the page.
      var sid = bar.getAttribute('data-section-id');
      var section = sid ? document.getElementById('shopify-section-' + sid) : null;
      var mainBtn = (section && $('.product-form__submit', section)) ||
                    $('.product-form__submit');

      var label = add.textContent.trim() || 'Add';

      // Keep the bar's variant + price in sync with the compact select.
      function syncFromSelect() {
        if (!sel) return;
        var opt = sel.options[sel.selectedIndex];
        if (!opt) return;
        add.setAttribute('data-variant-id', opt.value);
        if (price && opt.getAttribute('data-price')) {
          price.textContent = opt.getAttribute('data-price');
        }
        var soldOut = opt.disabled;
        add.disabled = !!soldOut;
        add.textContent = soldOut ? 'Sold out' : label;
        add.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      }
      on(sel, 'change', syncFromSelect);

      // Keep the sticky bar in lock-step with the MAIN variant picker. Without
      // this, picking a size in the main picker then scrolling down leaves the
      // sticky Add holding the seeded default variant → adds the WRONG size.
      // VariantSelects publishes PUB_SUB_EVENTS.variantChange (global.js); we
      // mirror its variant id, price, sold-out state, and the compact select.
      try {
        if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
            // Scope to THIS product section so a multi-product page never cross-wires.
            if (sid && event && event.data && event.data.sectionId &&
                String(event.data.sectionId) !== String(sid)) return;
            var variant = event && event.data && event.data.variant;
            if (!variant) {
              // No matching variant (option combo unavailable) → block the add.
              add.disabled = true;
              add.textContent = 'Sold out';
              add.setAttribute('aria-disabled', 'true');
              return;
            }
            add.setAttribute('data-variant-id', variant.id);
            if (price && variant.price != null) {
              price.textContent = '₦' + Math.round(variant.price / 100).toLocaleString('en-NG');
            }
            var soldOut = !variant.available;
            add.disabled = !!soldOut;
            add.textContent = soldOut ? 'Sold out' : label;
            add.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
            // mirror the compact select so the visible size matches the main picker
            if (sel) {
              for (var i = 0; i < sel.options.length; i++) {
                if (String(sel.options[i].value) === String(variant.id)) { sel.selectedIndex = i; break; }
              }
            }
          });
        }
      } catch (e) { /* sticky bar still works off its own select */ }

      // Add → AJAX add-to-cart via the shared helper.
      on(add, 'click', function () {
        var vid = add.getAttribute('data-variant-id');
        if (!vid || add.disabled) return;
        eonAddToCart({ id: vid, quantity: 1 }, eonBtnState(add, label));
      });

      function show() {
        bar.hidden = false;
        bar.setAttribute('aria-hidden', 'false');
        // next frame so the translateY transition runs
        requestAnimationFrame(function () { bar.classList.add('is-visible'); });
      }
      function hide() {
        bar.classList.remove('is-visible');
        bar.setAttribute('aria-hidden', 'true');
        if (REDUCE) { bar.hidden = true; }
        else { setTimeout(function () { if (!bar.classList.contains('is-visible')) bar.hidden = true; }, 300); }
      }

      // Only arm on mobile widths (the bar is display:none above 749px anyway).
      var MOBILE = '(max-width: 749px)';
      function armed() {
        try { return window.matchMedia && window.matchMedia(MOBILE).matches; }
        catch (e) { return false; }
      }

      if (mainBtn && 'IntersectionObserver' in window) {
        var sObs = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            if (!armed()) { hide(); return; }
            // Show once the main button has scrolled off the TOP of the viewport.
            var above = en.boundingClientRect.bottom < 0;
            if (!en.isIntersecting && above) show();
            else hide();
          });
        }, { threshold: 0 });
        sObs.observe(mainBtn);
      } else if (mainBtn) {
        // Fail-open fallback: simple scroll check.
        var onScroll = function () {
          if (!armed()) { hide(); return; }
          var r = mainBtn.getBoundingClientRect();
          if (r.bottom < 0) show(); else hide();
        };
        on(window, 'scroll', onScroll, { passive: true });
        onScroll();
      }
    });
  }

  /* ========================================================================
     INIT — scope to a section root when the editor hands us one.
     ===================================================================== */
  function initCoordSet(root) {
    var els = (root || document).querySelectorAll('[data-cx-coord]');
    Array.prototype.forEach.call(els, function (el) {
      if (el.__cxCoordBound) return; el.__cxCoordBound = true;
      var base = (el.getAttribute('data-eon-base') || '').replace(/\/+$/, '');
      var shop = el.getAttribute('data-eon-shop') || '';
      var pid = el.getAttribute('data-product-id') || '';
      var limit = el.getAttribute('data-limit') || '4';
      if (!base || !pid) return;
      var url = base + '/api/storefront/coord-set?shop=' + encodeURIComponent(shop) +
        '&product_id=' + encodeURIComponent(pid) + '&limit=' + encodeURIComponent(limit);
      var ctrl = ('AbortController' in window) ? new AbortController() : null;
      var to = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, 9000) : null;
      fetch(url, { mode: 'cors', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (to) { clearTimeout(to); to = null; }
          if (!d || !d.ok || !d.items || !d.items.length) return;  // never reveal an empty set
          var grid = el.querySelector('[data-eon-grid]');
          if (!grid) return;
          var nameEl = el.querySelector('[data-cx-coord-name]');
          if (nameEl && d.set_name) { nameEl.textContent = ' — ' + d.set_name; }
          var html = '', inStock = [], total = 0;
          d.items.forEach(function (p) {
            var href = p.handle ? ('/products/' + encodeURIComponent(p.handle)) : '#';
            var unit = (p.variant_price != null) ? p.variant_price : p.price_min;
            var price = (unit != null) ? ('₦' + Math.round(unit).toLocaleString('en-NG')) : '';
            if (p.variant_id) { inStock.push(p.variant_id); total += (unit || 0); }
            html += '<div class="cx-eon__card" data-handle="' + esc(p.handle || '') + '" data-pid="' + esc(p.shopify_id || '') + '">' +
              '<a class="cx-eon__link" href="' + href + '">' +
                '<div class="cx-eon__media">' + (p.image_url ? '<img src="' + esc(eonImg(p.image_url, 480)) + '" alt="' + esc(p.title) + '" loading="lazy" width="320" height="400">' : '') + '</div>' +
                '<div class="cx-eon__info"><span class="cx-eon__name">' + esc(p.title) + '</span>' +
                (price ? '<span class="cx-eon__price">' + price + '</span>' : '') + '</div>' +
              '</a>' +
              (p.variant_id ? '<button type="button" class="cx-eon__add" data-variant="' + esc(p.variant_id) + '" aria-label="Add ' + esc(p.title) + ' to cart">Add</button>' : '') +
              '</div>';
          });
          grid.innerHTML = html;
          grid.addEventListener('click', function (ev) {
            var addBtn = ev.target.closest && ev.target.closest('.cx-eon__add');
            if (addBtn) {
              ev.preventDefault();
              eonAddToCart({ id: addBtn.getAttribute('data-variant'), quantity: 1 }, eonBtnState(addBtn, 'Add'));
              return;
            }
            var card = ev.target.closest && ev.target.closest('.cx-eon__card');
            if (card && window.EON && window.EON.track) {
              window.EON.track('coord_set_click', { source_product_id: pid, product_shopify_id: card.getAttribute('data-pid') || '', product_handle: card.getAttribute('data-handle') || '' });
            }
          });
          if (inStock.length >= 2) {
            var foot = el.querySelector('[data-eon-foot]');
            if (foot) {
              var label = 'Add the set — ₦' + Math.round(total).toLocaleString('en-NG');
              foot.innerHTML = '<button type="button" class="cx-eon__addlook" aria-label="' + esc(label) + ' — adds ' + inStock.length + ' pieces to cart">' + label + '</button>';
              var lbtn = foot.querySelector('.cx-eon__addlook');
              lbtn.addEventListener('click', function () {
                eonAddToCart({ items: inStock.map(function (v) { return { id: v, quantity: 1 }; }) }, eonBtnState(lbtn, label));
              });
            }
          }
          el.hidden = false;
          requestAnimationFrame(function () { el.classList.add('is-in'); });
        })
        .catch(function () { if (to) { clearTimeout(to); } });
    });
  }

  /* ---- EON VISUAL SEARCH — "search by photo" (shop-the-look brain) -------
     Generalised so the SAME flow powers BOTH the search page and the header
     search drawer. Each [data-cx-vsearch-scope] block carries a trigger
     ([data-cx-vsearch-open]), a hidden file input ([data-cx-vsearch-file]) and
     a results panel ([data-cx-vsearch] with -grid / -status / -clear). The
     photo is downscaled in-browser (≤1280px — light on Lagos mobile data),
     POSTed to /api/storefront/shop-the-look, and the BEST match per detected
     garment is rendered (top / bottom / shoes / accessory) — a tentative match
     (confident:false) is labelled "closest" so an uncertain guess reads
     honestly. Gated on EON; fails OPEN (hides the trigger when disabled). */
  function initVisualSearch(root) {
    var C = window.EON_STOREFRONT;
    $all('[data-cx-vsearch-scope]', root).forEach(function (scope) {
      if (scope.__vsBound) { return; }
      var openBtn = $('[data-cx-vsearch-open]', scope);
      var file = $('[data-cx-vsearch-file]', scope);
      var box = $('[data-cx-vsearch]', scope);
      if (!openBtn || !file || !box) { return; }
      scope.__vsBound = 1;
      var grid = $('[data-cx-vsearch-grid]', box);
      var statusEl = $('[data-cx-vsearch-status]', box);
      var clearBtn = $('[data-cx-vsearch-clear]', box);
      if (!C || !C.enabled || !C.base) { openBtn.style.display = 'none'; return; }
      var base = String(C.base).replace(/\/+$/, ''), shop = encodeURIComponent(C.shop || '');

      function esc(s){ return String(s==null?'':s).replace(/[&<>"']/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c];}); }
      function money(n){ return n==null?'':'₦'+Math.round(n).toLocaleString('en-NG'); }
      function status(t){ statusEl.textContent=t||''; statusEl.hidden=!t; }
      function busy(o){ box.classList.toggle('is-busy',!!o); openBtn.disabled=!!o; }

      on(openBtn,'click',function(){ file.click(); });
      on(clearBtn,'click',function(){ box.hidden=true; if(grid){grid.innerHTML='';} status(''); file.value=''; });
      on(file,'change',function(){ var f=file.files&&file.files[0]; if(f){ process(f); } });

      function process(f){
        box.hidden=false; busy(true); if(grid){grid.innerHTML='';} status('Reading your photo…');
        var img=new Image(), url=URL.createObjectURL(f);
        img.onload=function(){
          var max=1280, w=img.width, h=img.height;
          if(w>=h && w>max){ h=Math.round(h*max/w); w=max; }
          else if(h>w && h>max){ w=Math.round(w*max/h); h=max; }
          var cv=document.createElement('canvas'); cv.width=w; cv.height=h;
          try{ cv.getContext('2d').drawImage(img,0,0,w,h); }catch(e){ URL.revokeObjectURL(url); return fail('Could not read that image.'); }
          URL.revokeObjectURL(url);
          var b64; try{ b64=cv.toDataURL('image/jpeg',0.85).split(',')[1]; }catch(e){ return fail('Could not read that image.'); }
          send(b64);
        };
        img.onerror=function(){ URL.revokeObjectURL(url); fail('Could not read that image.'); };
        img.src=url;
      }
      function fail(m){ busy(false); status(m||'Photo search is unavailable right now. Try a text search.'); }
      function send(b64){
        status('Finding the closest pieces in stock…');
        var ctrl=('AbortController'in window)?new AbortController():null;
        var to=ctrl?setTimeout(function(){try{ctrl.abort();}catch(e){}},25000):null;
        fetch(base+'/api/storefront/shop-the-look?shop='+shop, {
            method:'POST', mode:'cors', headers:{'Content-Type':'application/json'},
            body:JSON.stringify({image_b64:b64}), signal:ctrl?ctrl.signal:undefined })
          .then(function(r){ return r.ok?r.json():(r.status===429?{ok:false,status:'rate'}:null); })
          .then(function(d){
            if(to){clearTimeout(to);to=null;}
            busy(false);
            if(!d){ return fail('Could not search that photo. Try another image.'); }
            if(d.status==='rate'){ return status('One moment — too many photo searches just now. Try again shortly.'); }
            if(d.status==='daily_cap'){ return status('Photo search is resting for today — try a text search.'); }
            if(!d.ok){ return fail('Could not search that photo. Try another image.'); }
            var look=d.look||[];
            if(!look.length){ return status('No clear in-stock match. Try a closer, well-lit photo.'); }
            status('');
            if(grid){ grid.innerHTML=look.map(function(it){
              var p=it.product||{}; if(!p.handle){ return ''; }
              var slot=it.label||it.slot||'', soft=(it.confident===false);
              return '<a class="cx-vsearch__card'+(soft?' is-tentative':'')+'" href="/products/'+encodeURIComponent(p.handle)+'">'+
                (slot?'<span class="cx-vsearch__slot">'+esc(slot)+(soft?' · closest':'')+'</span>':'')+
                '<div class="cx-vsearch__media">'+(p.image_url?'<img src="'+esc(p.image_url)+'" alt="'+esc(p.title||'')+'" loading="lazy">':'')+'</div>'+
                '<div class="cx-vsearch__name">'+esc(p.title||'')+'</div>'+
                '<div class="cx-vsearch__price">'+money(p.price_min)+'</div></a>';
            }).join(''); }
            if(window.EON && window.EON.track){ window.EON.track('eon_visual_search',{matches:look.length}); }
          })
          .catch(function(){ if(to){clearTimeout(to);to=null;} fail('Could not reach photo search. Check your connection and retry.'); });
      }
    });
  }

  function bootAll(root, isInitialBoot) {
    var scope = root || document;
    // Reveal / countdown / try-on: our copies are redundant with the primary
    // crux-app.js port (which binds them once, document-wide, at page load and
    // does NOT re-run on shopify:section:load). So:
    //  • On the INITIAL boot, if the primary app is present it already owns the
    //    whole document — skip ours to avoid a double click handler on every
    //    [data-tryon] and a second setInterval tick loop on every [data-countdown].
    //  • On an editor re-insert (shopify:section:load — NOT initial), the primary
    //    app never re-binds the new section, so we MUST bind it here. Our own
    //    __cruxCountdownBound / __cruxTryonBound flags keep that idempotent.
    if (!(isInitialBoot && primaryAppOwns())) {
      bootReveal(scope);
      initCountdown(scope);
      initTryon(scope);
    }
    // Behaviours unique to this file always run.
    initFxSlider(scope);
    initEonStylist(scope);
    initCoordSet(scope);
    initSocialProof(scope);
    initCollectionBand(scope);
    initProductReviews(scope);
    initStickyATC(scope);
    initVisualSearch(scope);
  }

  ready(function () { bootAll(document, true); });

  // Zenith editor convention: re-init on section load + reorder (header-group
  // parity). event.target scopes init so re-inserts don't double-bind. These
  // are re-inserts, not the initial boot, so reveal/countdown/try-on DO bind
  // here (the primary app does not re-run for a re-inserted section).
  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target || document, false);
  });
  document.addEventListener('shopify:section:reorder', function (e) {
    bootAll(e.target || document, false);
  });
})();
